import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { assetsApi } from '../../api/assets';
import { genericDocumentApi } from '../../api/generic-documents';
import { ApiError } from '../../api/client';
import { SectionHeader } from '../../components/SectionHeader';
import { RecordHeader } from '../../components/RecordHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { WorkflowTrackingTab } from '../../components/WorkflowTrackingTab';
import { CommentsThread } from '../../components/CommentsThread';
import { DocumentsSection } from '../../components/DocumentsSection';
import { useToast } from '../../components/Toast';
import { EmployeePicker } from '../../components/EmployeePicker';
import { Modal } from '../../components/Modal';
import { TagLabel } from '../../components/TagLabel';
import { ASSET_STATUS_LABELS } from './AssetsListPage';
import { ASSET_STAGES } from '../../config/workflow-stages';
import { formatDate } from '../../utils/formatDate';

const CONDITION_OPTIONS = ['excellent', 'good', 'fair', 'poor', 'damaged'];

export function AssetWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const assetId = Number(id);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const { data: assetData, isError } = useQuery({ queryKey: ['asset', assetId], queryFn: () => assetsApi.get(assetId) });
  const asset = assetData?.data;
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['asset', assetId] });
  const { data: docsData } = useQuery({ queryKey: ['asset-documents', assetId], queryFn: () => genericDocumentApi.listFor('asset', assetId) });

  const { data: assignmentsData } = useQuery({ queryKey: ['asset-assignments', assetId], queryFn: () => assetsApi.listAssignments(assetId) });
  const assignments = assignmentsData?.data ?? [];
  const activeAssignment = assignments.find((a) => !a.actual_return_date);

  const [assignForm, setAssignForm] = useState({ assignedToType: 'staff', assignedToEmployeeId: '', assignedToLabel: '', assignmentDate: '', conditionAtIssue: 'good' });
  const assignMutation = useMutation({
    mutationFn: (input: Record<string, unknown>) => assetsApi.assign(assetId, input),
    onSuccess: () => { showSuccess('Asset assigned.'); queryClient.invalidateQueries({ queryKey: ['asset-assignments', assetId] }); invalidate(); },
    onError: (err: ApiError) => showError(err.message),
  });
  const returnMutation = useMutation({
    mutationFn: ({ assignmentId, conditionAtReturn }: { assignmentId: number; conditionAtReturn: string }) =>
      assetsApi.returnAssignment(assetId, assignmentId, { actualReturnDate: new Date().toISOString().slice(0, 10), conditionAtReturn }),
    onSuccess: () => { showSuccess('Return recorded.'); queryClient.invalidateQueries({ queryKey: ['asset-assignments', assetId] }); invalidate(); },
    onError: (err: ApiError) => showError(err.message),
  });

  const { data: movementsData } = useQuery({ queryKey: ['asset-movements', assetId], queryFn: () => assetsApi.listMovements(assetId) });
  const movements = movementsData?.data ?? [];
  const [movementForm, setMovementForm] = useState({ originLabel: '', destinationLabel: '', responsibleEmployeeId: '', dispatchDate: '', conditionOnDispatch: 'good' });
  const createMovementMutation = useMutation({
    mutationFn: (input: Record<string, unknown>) => assetsApi.createMovement(assetId, input),
    onSuccess: () => { showSuccess('Movement recorded.'); queryClient.invalidateQueries({ queryKey: ['asset-movements', assetId] }); invalidate(); },
    onError: (err: ApiError) => showError(err.message),
  });
  const receiveMovementMutation = useMutation({
    mutationFn: ({ movementId, conditionOnReceipt }: { movementId: number; conditionOnReceipt: string }) =>
      assetsApi.receiveMovement(assetId, movementId, { receiptDate: new Date().toISOString().slice(0, 10), conditionOnReceipt }),
    onSuccess: () => { showSuccess('Receipt recorded.'); queryClient.invalidateQueries({ queryKey: ['asset-movements', assetId] }); invalidate(); },
    onError: (err: ApiError) => showError(err.message),
  });

  const { data: disposalData } = useQuery({ queryKey: ['asset-disposal', assetId], queryFn: () => assetsApi.getDisposal(assetId), retry: false });
  const disposal = disposalData?.data;
  const [disposalForm, setDisposalForm] = useState({ reason: '', conditionAtDisposal: 'poor', recommendedMethod: 'write_off' });
  const [showTagModal, setShowTagModal] = useState(false);
  const createDisposalMutation = useMutation({
    mutationFn: (input: Record<string, unknown>) => assetsApi.createDisposal(assetId, input),
    onSuccess: () => { showSuccess('Disposal record created.'); queryClient.invalidateQueries({ queryKey: ['asset-disposal', assetId] }); },
    onError: (err: ApiError) => showError(err.message),
  });
  const disposalActionMutation = useMutation({
    mutationFn: (action: 'submit' | 'approve') => (action === 'submit' ? assetsApi.submitDisposal(disposal!.id) : assetsApi.approveDisposal(disposal!.id)),
    onSuccess: () => { showSuccess('Disposal updated.'); queryClient.invalidateQueries({ queryKey: ['asset-disposal', assetId] }); invalidate(); },
    onError: (err: ApiError) => showError(err.message),
  });

  if (isError) return <div className="page"><div className="error-state">Failed to load this asset. Please try refreshing the page.</div></div>;
  if (!asset) return <div className="page"><div className="loading-state"><span className="loading-spinner" />Loading…</div></div>;

  return (
    <div className="page">
      <RecordHeader
        title={asset.asset_number}
        readOnly={false}
        entityType="asset"
        entityId={assetId}
        actions={<button className="btn-secondary" onClick={() => setShowTagModal(true)}>Print Tag</button>}
        fields={[
          { label: 'Name', value: asset.name },
          { label: 'Type', value: asset.asset_type ?? '-' },
          { label: 'Condition', value: asset.condition_status },
          { label: 'Status', value: <StatusBadge status={asset.status} label={ASSET_STATUS_LABELS[asset.status]} /> },
        ]}
      />

      {showTagModal && (
        <Modal title="Asset Tag" onClose={() => setShowTagModal(false)}>
          <TagLabel code={asset.asset_number} title={asset.name} subtitle={asset.asset_type ?? undefined} />
          <div className="form-actions">
            <button onClick={() => window.print()}>Print</button>
          </div>
        </Modal>
      )}

      <WorkspaceTabs
        tabs={[
          {
            key: 'overview', label: 'Overview', content: (
              <div>
                <SectionHeader title="Asset Information" />
                <dl className="detail-grid">
                  <dt>Manufacturer</dt><dd>{asset.manufacturer ?? '-'}</dd>
                  <dt>Model</dt><dd>{asset.model ?? '-'}</dd>
                  <dt>Serial Number</dt><dd>{asset.serial_number ?? '-'}</dd>
                  <dt>Supplier</dt><dd>{asset.supplier ?? '-'}</dd>
                  <dt>Purchase Date</dt><dd>{asset.purchase_date ? formatDate(asset.purchase_date) : '-'}</dd>
                  <dt>Purchase Cost</dt><dd>{asset.purchase_cost ? `${asset.purchase_cost_currency} ${asset.purchase_cost}` : '-'}</dd>
                  <dt>Warranty</dt><dd>{asset.warranty_start && asset.warranty_end ? `${formatDate(asset.warranty_start)} – ${formatDate(asset.warranty_end)}` : '-'}</dd>
                </dl>
              </div>
            ),
          },
          {
            key: 'assignment', label: 'Assignment', content: (
              <div>
                <SectionHeader title="Assignment History" />
                <ul>
                  {assignments.map((a) => (
                    <li key={a.id}>
                      {a.assigned_to_label ?? `Employee #${a.assigned_to_employee_id}`} ({a.assigned_to_type}) · issued {formatDate(a.assignment_date)}, condition {a.condition_at_issue}
                      {a.actual_return_date ? ` · returned ${formatDate(a.actual_return_date)}, condition ${a.condition_at_return}` : (
                        <> · <button onClick={() => { const c = prompt('Condition at return?', 'good'); if (c) returnMutation.mutate({ assignmentId: a.id, conditionAtReturn: c }); }}>Record Return</button></>
                      )}
                    </li>
                  ))}
                </ul>
                {!activeAssignment && (
                  <form className="inline-form" onSubmit={(e) => {
                    e.preventDefault();
                    const input: Record<string, unknown> = { assignedToType: assignForm.assignedToType, assignmentDate: assignForm.assignmentDate, conditionAtIssue: assignForm.conditionAtIssue };
                    if (assignForm.assignedToType === 'staff' && assignForm.assignedToEmployeeId) input.assignedToEmployeeId = Number(assignForm.assignedToEmployeeId);
                    else input.assignedToLabel = assignForm.assignedToLabel;
                    assignMutation.mutate(input);
                  }}>
                    <select value={assignForm.assignedToType} onChange={(e) => setAssignForm((s) => ({ ...s, assignedToType: e.target.value }))}>
                      <option value="staff">Staff</option><option value="office">Office</option><option value="department">Department</option>
                      <option value="project">Project</option><option value="field_site">Field Site</option><option value="vehicle">Vehicle</option>
                    </select>
                    {assignForm.assignedToType === 'staff' ? (
                      <EmployeePicker value={assignForm.assignedToEmployeeId} onChange={(e) => setAssignForm((s) => ({ ...s, assignedToEmployeeId: e.target.value }))} />
                    ) : (
                      <input placeholder="Description" value={assignForm.assignedToLabel} onChange={(e) => setAssignForm((s) => ({ ...s, assignedToLabel: e.target.value }))} />
                    )}
                    <input type="date" value={assignForm.assignmentDate} onChange={(e) => setAssignForm((s) => ({ ...s, assignmentDate: e.target.value }))} required />
                    <select value={assignForm.conditionAtIssue} onChange={(e) => setAssignForm((s) => ({ ...s, conditionAtIssue: e.target.value }))}>
                      {CONDITION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <button type="submit" disabled={assignMutation.isPending}>Assign</button>
                  </form>
                )}
              </div>
            ),
          },
          {
            key: 'movement', label: 'Movement', content: (
              <div>
                <SectionHeader title="Movement History" />
                <ul>
                  {movements.map((m) => (
                    <li key={m.id}>
                      {m.origin_label} → {m.destination_label} · dispatched {formatDate(m.dispatch_date)}, condition {m.condition_on_dispatch}
                      {m.receipt_date ? ` · received ${formatDate(m.receipt_date)}, condition ${m.condition_on_receipt}` : (
                        <> · <button onClick={() => { const c = prompt('Condition on receipt?', 'good'); if (c) receiveMovementMutation.mutate({ movementId: m.id, conditionOnReceipt: c }); }}>Record Receipt</button></>
                      )}
                    </li>
                  ))}
                </ul>
                <form className="inline-form" onSubmit={(e) => { e.preventDefault(); createMovementMutation.mutate({ ...movementForm, responsibleEmployeeId: Number(movementForm.responsibleEmployeeId) }); }}>
                  <input placeholder="Origin" value={movementForm.originLabel} onChange={(e) => setMovementForm((s) => ({ ...s, originLabel: e.target.value }))} required />
                  <input placeholder="Destination" value={movementForm.destinationLabel} onChange={(e) => setMovementForm((s) => ({ ...s, destinationLabel: e.target.value }))} required />
                  <EmployeePicker value={movementForm.responsibleEmployeeId} onChange={(e) => setMovementForm((s) => ({ ...s, responsibleEmployeeId: e.target.value }))} />
                  <input type="date" value={movementForm.dispatchDate} onChange={(e) => setMovementForm((s) => ({ ...s, dispatchDate: e.target.value }))} required />
                  <select value={movementForm.conditionOnDispatch} onChange={(e) => setMovementForm((s) => ({ ...s, conditionOnDispatch: e.target.value }))}>
                    {CONDITION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <button type="submit" disabled={createMovementMutation.isPending}>Dispatch</button>
                </form>
              </div>
            ),
          },
          {
            key: 'disposal', label: 'Disposal', content: (
              <div>
                <SectionHeader title="Disposal" />
                {disposal ? (
                  <div>
                    <dl className="detail-grid">
                      <dt>Reference</dt><dd>{disposal.disposal_reference}</dd>
                      <dt>Reason</dt><dd>{disposal.reason}</dd>
                      <dt>Method</dt><dd>{disposal.recommended_method}</dd>
                      <dt>Status</dt><dd><StatusBadge status={disposal.status} /></dd>
                    </dl>
                    {disposal.status === 'draft' && <button onClick={() => disposalActionMutation.mutate('submit')}>Submit</button>}
                    {['submitted', 'under_review'].includes(disposal.status) && <button onClick={() => disposalActionMutation.mutate('approve')}>Approve</button>}
                  </div>
                ) : (
                  <form className="inline-form" onSubmit={(e) => { e.preventDefault(); createDisposalMutation.mutate(disposalForm); }}>
                    <textarea placeholder="Reason for disposal" value={disposalForm.reason} onChange={(e) => setDisposalForm((s) => ({ ...s, reason: e.target.value }))} required />
                    <select value={disposalForm.conditionAtDisposal} onChange={(e) => setDisposalForm((s) => ({ ...s, conditionAtDisposal: e.target.value }))}>
                      {CONDITION_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select value={disposalForm.recommendedMethod} onChange={(e) => setDisposalForm((s) => ({ ...s, recommendedMethod: e.target.value }))}>
                      <option value="transfer">Transfer</option><option value="donation">Donation</option><option value="sale">Sale</option>
                      <option value="write_off">Write-off</option><option value="destruction">Destruction</option><option value="return_to_donor">Return to Donor</option>
                    </select>
                    <button type="submit" disabled={createDisposalMutation.isPending}>Initiate Disposal</button>
                  </form>
                )}
              </div>
            ),
          },
          {
            key: 'documents', label: 'Documents', content: (
              <DocumentsSection
                documents={docsData?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('asset', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('asset', assetId, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('asset', documentId)}
                queryKeyToInvalidate={['asset-documents', assetId]}
                hrCategories={false}
              />
            ),
          },
          {
            key: 'tracking', label: 'Tracking', content: (
              <div>
                <WorkflowTrackingTab entityType="asset" entityId={assetId} actionStageMap={ASSET_STAGES} statusLabels={ASSET_STATUS_LABELS} />
                <CommentsThread entityType="asset" entityId={assetId} section="assets" heading="Comments for this Asset" />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
